/**
 * @lines98/client/components/scoring-ladder — «Лестница очков» (Scoring Ladder, идея B).
 *
 * ЗАЧЕМ / WHY THIS EXISTS (owner-direction 2026-06-21): справочная таблица очков
 * («длина линии → очки», 5→5,6→11,7→18,8→26,9→35) переделана из плоской «белой плашки»
 * (прежнее owner-правило 2026-06-21 п.10б, content.css §line~256) в ВИЗУАЛЬНЫЙ БЛОК В
 * СТИЛЕ ЛИДЕРБОРДА: каждая длина линии — строка-плашка с РЕАЛЬНОЙ линией из n игровых
 * шаров слева + крупным значением очков справа + декоративным баром роста; верхняя по
 * ценности строка (n=9) подсвечена как «строка-победитель» (🥇 + аннотация кратности).
 * Новое owner-направление ЗАМЕНЯЕТ прежнее «таблица очков — белая плашка» — теперь
 * тема-aware плашка на тех же токенах, что лидерборд (--panel/--ink/--accent).
 *
 * SCOPE:
 *   - Переиспользуемый примитив-фабрика `createScoringLadder({lang, lineHeader, pointsHeader})`
 *     → {element, destroy}.
 *   - Строит DOM «лестницы» (5 строк n=5..9) из ИГРОВЫХ шаров (.ball static .bcK) и
 *     тема-токенов; числа очков считает из КАНОН-формулы `scoreFor(n)` движка (НЕ хардкод
 *     5/11/18/26/35 — CLAUDE.md no-hardcode + §5 один движок клиент=сервер).
 *   - ВИДИМЫЕ заголовки колонок (lineHeader/pointsHeader) НЕ хранятся здесь — приходят
 *     параметром из ИСХОДНОЙ markdown-таблицы content/<lang>/scoring.md (единый источник;
 *     content-screen читает <th> заменяемой таблицы и передаёт их). Локально в COPY —
 *     ТОЛЬКО aria-строки (regionLabel/rowLabel), которых в таблице нет.
 * NOT IN SCOPE:
 *   - Удаление/правка исходного markdown `<table>` (остаётся в bundle для SEO/no-JS/a11y).
 *     Монтированием/заменой ведает content-screen (тонкая ветка).
 *   - Анимации/инфинит-эффекты, новые серверные поля.
 *
 * Переиспользование (code-structure §1 «ствол → ветки», §2 примитивы): шарики — ТА ЖЕ
 * разметка доски (`.ball.static.bcK` → --bc → --ball-N, board.css), размер шарика —
 * локальный override `--ball-size` на хосте формулой (меньше доски: много в ряд), как
 * делают mode-card/skin-gallery. Визуальный язык строк взят у лидерборда (leaderboard.css)
 * на тех же тема-токенах — БЕЗ копирования CSS.
 *
 * Cross-references: shared engine `scoreFor` (lines98.ts §scoreFor); board.css (.ball/.bcK/
 *   --bc/--ball-N); leaderboard.css (визуальный язык строк-плашек); content-screen.ts
 *   (монтаж/прогрессивная замена); responsive-ui §0.A P1 / §0.G / §5; code-structure §1/§2/§4.
 */

import './scoring-ladder.css';

import { scoreFor } from '@lines98/shared/engine/lines98';
import type { ContentLang } from '@lines98/shared/config/seo';

/**
 * Поддерживаемые языки лестницы = ВСЕ контент-языки (CONTENT_LANGS). Тип берём из
 * единого источника (shared/config/seo), НЕ хардкодим union — расширение списка
 * языков ловится компилятором (нужно дополнить COPY aria-строк). Видимые заголовки
 * колонок здесь НЕ живут — они приходят из исходной таблицы content/<lang>/scoring.md
 * (true single source; owner-баг 2026-06-23: «никакого русского шлейфа; нетекстовые
 * элементы одинаковы на всех языках, подписи — на языке страницы»).
 */
export type ScoringLadderLang = ContentLang;

/** Хэндл смонтированной лестницы — element для вставки + destroy для размонтирования. */
export interface ScoringLadderHandle {
  /** Корневой узел лестницы (вставляет content-screen вместо/рядом с таблицей). */
  readonly element: HTMLElement;
  /** Размонтировать: снять ссылки/узлы (слушателей у лестницы нет — статичный блок). */
  destroy(): void;
}

interface LadderCopy {
  /** aria-label всего блока (роль: справочная таблица очков). */
  readonly regionLabel: string;
  /** Шаблон aria-описания строки: n шаров → очки. */
  rowLabel(n: number, pts: number): string;
}

/**
 * Локальная мини-мапа aria-строк на ВСЕ контент-языки. Держим ЗДЕСЬ, а не в общем
 * каталоге: это частный a11y-контент примитива (write-time-дисциплина — не плодить
 * глобальные ключи под одноразовый блок). При расширении CONTENT_LANGS компилятор
 * потребует дополнить мапу (Record<ScoringLadderLang, …>) — так добавлены tr/id/th
 * 2026-06-25 (короткие factual a11y-подписи на языке страницы, не body-copy).
 *
 * Здесь ТОЛЬКО aria (`regionLabel`/`rowLabel`) — описательные строки, которых НЕТ в
 * таблице. ВИДИМЫЕ заголовки колонок (длина линии / очки) НЕ дублируются здесь: они
 * приходят параметром из исходной markdown-таблицы content/<lang>/scoring.md (единый
 * источник — рассинхрон структурно невозможен; owner-баг 2026-06-23).
 */
const COPY: Record<ScoringLadderLang, LadderCopy> = {
  ru: {
    regionLabel: 'Очки за длину линии',
    rowLabel: (n, pts) => `Линия из ${n} шаров — ${pts} очков`,
  },
  en: {
    regionLabel: 'Points by line length',
    rowLabel: (n, pts) => `Line of ${n} balls — ${pts} points`,
  },
  de: {
    regionLabel: 'Punkte nach Linienlänge',
    rowLabel: (n, pts) => `Linie aus ${n} Kugeln — ${pts} Punkte`,
  },
  es: {
    regionLabel: 'Puntos según la longitud de la línea',
    rowLabel: (n, pts) => `Línea de ${n} bolas — ${pts} puntos`,
  },
  fr: {
    regionLabel: 'Points selon la longueur de ligne',
    rowLabel: (n, pts) => `Ligne de ${n} billes — ${pts} points`,
  },
  it: {
    regionLabel: 'Punti per lunghezza della linea',
    rowLabel: (n, pts) => `Linea di ${n} palline — ${pts} punti`,
  },
  pt: {
    regionLabel: 'Pontos por comprimento da linha',
    rowLabel: (n, pts) => `Linha de ${n} bolas — ${pts} pontos`,
  },
  ja: {
    regionLabel: 'ラインの長さごとの得点',
    rowLabel: (n, pts) => `${n}個のボールのライン — ${pts}点`,
  },
  ko: {
    regionLabel: '라인 길이별 점수',
    rowLabel: (n, pts) => `공 ${n}개의 라인 — ${pts}점`,
  },
  vi: {
    regionLabel: 'Điểm theo độ dài đường',
    rowLabel: (n, pts) => `Đường gồm ${n} bóng — ${pts} điểm`,
  },
  'zh-CN': {
    regionLabel: '按连线长度计分',
    rowLabel: (n, pts) => `${n} 个球的连线 — ${pts} 分`,
  },
  'zh-TW': {
    regionLabel: '按連線長度計分',
    rowLabel: (n, pts) => `${n} 個球的連線 — ${pts} 分`,
  },
  tr: {
    regionLabel: 'Çizgi uzunluğuna göre puanlar',
    rowLabel: (n, pts) => `${n} toptan oluşan çizgi — ${pts} puan`,
  },
  id: {
    regionLabel: 'Poin berdasarkan panjang garis',
    rowLabel: (n, pts) => `Garis dari ${n} bola — ${pts} poin`,
  },
  th: {
    regionLabel: 'คะแนนตามความยาวของเส้น',
    rowLabel: (n, pts) => `เส้นจากลูกบอล ${n} ลูก — ${pts} คะแนน`,
  },
};

/** Длины линий, выводимые лестницей — сверху (ценнее) вниз: 9,8,7,6,5. */
const LINE_LENGTHS = [9, 8, 7, 6, 5] as const;

/**
 * Назначение цвета шара РЯДУ для живости. Реальная линия одноцветна, поэтому ВСЕ шары
 * в одном ряду — ОДНОГО цвета (только между рядами цвета разные). Палитра — игровые
 * `.bc1..7` (board.css → --ball-1..7, global.css).
 *
 * Выбор цвета строки-победителя (n=9): bc4 = --ball-4 = #e8b22e — это ЗОЛОТИСТО-ЖЁЛТЫЙ
 * (наиболее «золотой»/контрастный в палитре, перекликается с 🥇), отсюда n=9 → bc4.
 * Прочие ряды разнесены по палитре: 5→bc3 (синий), 6→bc2 (зелёный), 7→bc6 (бирюза),
 * 8→bc1 (красный), 9→bc4 (золото). Маппинг по n (не по индексу) — детерминирован.
 */
const ROW_COLOR: Record<number, number> = {
  5: 3,
  6: 2,
  7: 6,
  8: 1,
  9: 4,
};

/**
 * Создать «Лестницу очков». Чистая фабрика DOM (vanilla TS, code-structure §2): строит
 * узлы напрямую, размеры/цвета — токенами/формулами (.bcK + --ball-size override в CSS),
 * числа — из движка (`scoreFor`). Без сайд-эффектов кроме построения отсоединённого DOM.
 */
export function createScoringLadder(opts: {
  lang: ScoringLadderLang;
  /**
   * Видимые заголовки колонок — ЕДИНЫЙ ИСТОЧНИК: берутся из исходной markdown-таблицы
   * content/<lang>/scoring.md (content-screen читает <th> заменяемой таблицы и передаёт
   * сюда). Параметр обязателен — нельзя отрисовать лестницу не сославшись на ту таблицу,
   * что она замещает → рассинхрон структурно невозможен (owner-баг 2026-06-23).
   */
  lineHeader: string;
  pointsHeader: string;
}): ScoringLadderHandle {
  const copy = COPY[opts.lang] ?? COPY.en;

  // Максимум очков (n=9) — масштаб для доли бара роста (pts/topScore). Из канон-формулы.
  const topScore = scoreFor(9);

  const root = document.createElement('div');
  root.className = 'scoring-ladder';
  root.setAttribute('role', 'table');
  root.setAttribute('aria-label', copy.regionLabel);

  // Мелкая uppercase шапка-колонки (визуальный язык .leaderboard-header-row, на токенах).
  const header = document.createElement('div');
  header.className = 'scoring-ladder-head';
  header.setAttribute('role', 'row');
  const headLine = document.createElement('span');
  headLine.className = 'scoring-ladder-head-line';
  headLine.setAttribute('role', 'columnheader');
  headLine.textContent = opts.lineHeader;
  const headPoints = document.createElement('span');
  headPoints.className = 'scoring-ladder-head-points';
  headPoints.setAttribute('role', 'columnheader');
  headPoints.textContent = opts.pointsHeader;
  header.append(headLine, headPoints);
  root.appendChild(header);

  for (const n of LINE_LENGTHS) {
    const pts = scoreFor(n);
    const isTop = n === 9;

    const row = document.createElement('div');
    row.className = isTop ? 'scoring-ladder-row scoring-ladder-row--top' : 'scoring-ladder-row';
    row.setAttribute('role', 'row');
    // Доступная подпись строки целиком (шарики декоративны → aria-hidden ниже).
    row.setAttribute('aria-label', copy.rowLabel(n, pts));

    // ── ЛЕВО: линия из n игровых шаров (одного цвета). ────────────────────────────
    const line = document.createElement('div');
    line.className = 'scoring-ladder-line';
    line.setAttribute('role', 'cell');
    line.setAttribute('aria-hidden', 'true'); // визуальный ряд; смысл несёт aria-label строки
    const color = ROW_COLOR[n] ?? 1;
    for (let i = 0; i < n; i++) {
      const ball = document.createElement('div');
      // ТА ЖЕ разметка доски: .ball.static.bcK (цвет через .bcK → --bc → --ball-N).
      ball.className = `ball static bc${color}`;
      line.appendChild(ball);
    }

    // ── ПРАВО: бар роста (декоративный) + значение очков (крупно, tabular-nums). ───
    const meta = document.createElement('div');
    meta.className = 'scoring-ladder-meta';
    meta.setAttribute('role', 'cell');

    // Бар роста: длина пропорциональна очкам (макс = scoreFor(9)). ДЕКОРАТИВНЫЙ (P3/P4):
    // ширину задаём через --bar (доля), CSS на узких viewport бар скрывает (must-visible —
    // только шарики+очки, §0.A P1). aria-hidden — смысла не несёт.
    const bar = document.createElement('div');
    bar.className = 'scoring-ladder-bar';
    bar.setAttribute('aria-hidden', 'true');
    // Доля относительно максимума (n=9). 0..1 → CSS ширина бара.
    bar.style.setProperty('--bar', String(pts / topScore));

    const value = document.createElement('span');
    value.className = 'scoring-ladder-value';
    value.textContent = String(pts);

    // Медаль 🥇 и аннотация кратности «×K» УБРАНЫ (owner-directive 2026-06-21: «убрать
    // медаль и убрать ×7 — сбивает»). Строка-победитель (n=9) отличается только подсветкой
    // плашки (--top, accent-soft) — как viewer-row лидерборда. Состав одинаков: бар + очки.
    meta.append(bar, value);

    row.append(line, meta);
    root.appendChild(row);
  }

  return {
    element: root,
    destroy() {
      // Статичный блок без слушателей/таймеров — снимаем узел из DOM (если ещё в дереве).
      root.remove();
    },
  };
}
